import React, { useState, useEffect, useRef } from 'react';
import { shopLocationService } from '../services/shopLocationService';
import { invalidateLocationCache } from './SlideCard';
import { ShopLocation } from '../types';

interface LocationPlaceEditorProps {
  locationId?: string;
  placeId?: string;
  onLocationChange: (locationId: string | undefined) => void;
  onPlaceChange: (placeId: string | undefined) => void;
  shopId: string;
  className?: string;
  allowCreate?: boolean; // Whether to allow creating new locations/places (default: true)
  compact?: boolean;
}

interface LocationPlaceOption {
  type: 'location' | 'place';
  locationId: string;
  locationName: string;
  placeId?: string;
  placeName?: string;
  displayText: string;
  isNew: boolean;
}

const LocationPlaceEditor: React.FC<LocationPlaceEditorProps> = ({
  locationId,
  placeId,
  onLocationChange,
  onPlaceChange,
  shopId,
  className = '',
  allowCreate = true,
  compact = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [locations, setLocations] = useState<ShopLocation[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<LocationPlaceOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [currentSelection, setCurrentSelection] = useState<LocationPlaceOption | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        const locs = await shopLocationService.getShopLocations(shopId, 0, 100, 'name', 'ASC');
        setLocations(locs.filter(loc => loc.active));
      } catch (error) {
        console.error('Failed to fetch shop locations:', error);
        setLocations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [shopId]);

  // Set current selection based on props
  useEffect(() => {
    console.log('=== LocationPlaceEditor - Initializing selection ===');
    console.log('Props received:', { placeId, locationId });
    console.log('Available locations:', {
      locationsCount: locations.length,
      locations: locations.map(l => ({
        id: l.id,
        name: l.name,
        placesCount: l.places?.length || 0,
        placeIds: l.places?.map(p => p.id) || []
      }))
    });

    if (placeId && locations.length > 0) {
      // Find the place across all locations
      for (const location of locations) {
        const place = location.places?.find(p => p.id === placeId);
        if (place) {
          console.log('LocationPlaceEditor - Found place:', {
            placeId: place.id,
            placeName: place.name,
            locationId: location.id,
            locationName: location.name
          });
          setCurrentSelection({
            type: 'place',
            locationId: location.id,
            locationName: location.name,
            placeId: place.id,
            placeName: place.name,
            displayText: `${location.name} :: ${place.name}`,
            isNew: false
          });
          return;
        }
      }
      console.warn('LocationPlaceEditor - Place not found:', placeId);
    } else if (locationId && locations.length > 0) {
      const location = locations.find(l => l.id === locationId);
      if (location) {
        console.log('LocationPlaceEditor - Found location:', {
          locationId: location.id,
          locationName: location.name
        });
        setCurrentSelection({
          type: 'location',
          locationId: location.id,
          locationName: location.name,
          displayText: `${location.name}`,
          isNew: false
        });
        return;
      }
      console.warn('LocationPlaceEditor - Location not found:', locationId);
    }

    setCurrentSelection(null);
  }, [locationId, placeId, locations]);

  // Filter options as user types
  useEffect(() => {
    // If location is selected (refinement mode), show only places within that location
    if (currentSelection?.type === 'location') {
      const selectedLocation = locations.find(l => l.id === currentSelection.locationId);
      if (selectedLocation) {
        const query = inputValue.trim().toLowerCase();
        const options: LocationPlaceOption[] = [];

        selectedLocation.places?.forEach(place => {
          const placeNameLower = place.name.toLowerCase();
          if (!query || placeNameLower.includes(query)) {
            options.push({
              type: 'place',
              locationId: selectedLocation.id,
              locationName: selectedLocation.name,
              placeId: place.id,
              placeName: place.name,
              displayText: `${selectedLocation.name} :: ${place.name}`,
              isNew: false
            });
          }
        });

        setFilteredOptions(options);
        // Show dropdown if user is typing AND there are options (or could create new if allowed)
        setShowDropdown(inputValue.trim().length > 0 && (options.length > 0 || allowCreate));
        setSelectedIndex(-1);
      }
      return;
    }

    // Normal mode: no selection yet
    if (inputValue.trim()) {
      const query = inputValue.trim().toLowerCase();
      const options: LocationPlaceOption[] = [];

      // Check if looking for a place (contains →)
      const isPlaceSearch = query.includes('::') || query.includes('→') || query.includes('->');

      locations.forEach(location => {
        const locationNameLower = location.name.toLowerCase();

        if (isPlaceSearch) {
          // User is searching for places - show matching places
          const parts = query.split(/::|\u2192|->/).map(p => p.trim());
          const locationQuery = parts[0];
          const placeQuery = parts[1] || '';

          if (locationNameLower.includes(locationQuery)) {
            location.places?.forEach(place => {
              const placeNameLower = place.name.toLowerCase();
              if (!placeQuery || placeNameLower.includes(placeQuery)) {
                options.push({
                  type: 'place',
                  locationId: location.id,
                  locationName: location.name,
                  placeId: place.id,
                  placeName: place.name,
                  displayText: `${location.name} :: ${place.name}`,
                  isNew: false
                });
              }
            });
          }
        } else {
          // User is searching for location or place by name
          const locationMatches = locationNameLower.includes(query);

          // Add matching location
          if (locationMatches) {
            options.push({
              type: 'location',
              locationId: location.id,
              locationName: location.name,
              displayText: `${location.name}`,
              isNew: false
            });
          }

          // Add places that match query OR belong to matching location
          location.places?.forEach(place => {
            const placeNameLower = place.name.toLowerCase();
            const placeMatches = placeNameLower.includes(query);

            // Show place if: location matches OR place name matches
            if (locationMatches || placeMatches) {
              options.push({
                type: 'place',
                locationId: location.id,
                locationName: location.name,
                placeId: place.id,
                placeName: place.name,
                displayText: `${location.name} :: ${place.name}`,
                isNew: false
              });
            }
          });
        }
      });

      setFilteredOptions(options);
      setShowDropdown(options.length > 0 || inputValue.trim().length > 0);
      setSelectedIndex(-1);
    } else {
      setFilteredOptions([]);
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  }, [inputValue, locations, currentSelection, allowCreate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClearSelection = () => {
    setCurrentSelection(null);
    onLocationChange(undefined);
    onPlaceChange(undefined);
    setInputValue('');
  };

  const handleSelectOption = async (option: LocationPlaceOption) => {
    if (option.isNew) {
      // Create new location or place
      await handleCreateNew(option);
    } else {
      // Select existing location or place
      setCurrentSelection(option);

      if (option.type === 'location') {
        // Location selected - allow refinement to place
        onLocationChange(option.locationId);
        onPlaceChange(undefined);
        setInputValue(''); // Clear for new search within location
        setShowDropdown(false);
        setSelectedIndex(-1);
        // Keep input active for place selection
        setTimeout(() => inputRef.current?.focus(), 0);
      } else {
        // Place selected - final selection
        onLocationChange(undefined);
        onPlaceChange(option.placeId);
        setInputValue('');
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    }
  };

  const handleCreateNew = async (option: LocationPlaceOption) => {
    try {
      setLoading(true);

      if (option.type === 'location') {
        // Create new location
        const newLocation = await shopLocationService.createShopLocation(shopId, {
          name: option.locationName,
          description: ''
        });

        // Refresh locations and invalidate the shared cache so SlideCards pick up new data
        invalidateLocationCache(shopId);
        const updatedLocations = await shopLocationService.getShopLocations(shopId, 0, 100, 'name', 'ASC');
        setLocations(updatedLocations.filter(loc => loc.active));

        // Select the new location
        const createdOption: LocationPlaceOption = {
          type: 'location',
          locationId: newLocation.id,
          locationName: newLocation.name,
          displayText: `${newLocation.name}`,
          isNew: false
        };
        setCurrentSelection(createdOption);
        onLocationChange(newLocation.id);
        onPlaceChange(undefined);
      } else {
        // Create new place
        const newPlace = await shopLocationService.addPlace(shopId, option.locationId, {
          name: option.placeName!,
          description: ''
        });

        // Refresh locations and invalidate the shared cache so SlideCards pick up new data
        invalidateLocationCache(shopId);
        const updatedLocations = await shopLocationService.getShopLocations(shopId, 0, 100, 'name', 'ASC');
        setLocations(updatedLocations.filter(loc => loc.active));

        // Select the new place
        const createdOption: LocationPlaceOption = {
          type: 'place',
          locationId: option.locationId,
          locationName: option.locationName,
          placeId: newPlace.id,
          placeName: newPlace.name,
          displayText: `${option.locationName} :: ${newPlace.name}`,
          isNew: false
        };
        setCurrentSelection(createdOption);
        onLocationChange(undefined);
        onPlaceChange(newPlace.id);
      }

      setInputValue('');
      setShowDropdown(false);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Failed to create new location/place:', error);
      alert('Failed to create new location/place. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
        // Select from dropdown
        handleSelectOption(filteredOptions[selectedIndex]);
      } else if (inputValue.trim() && allowCreate) {
        // Create new location or place (only if allowCreate is true)
        handleCreateNew(parseInputAsNew(inputValue.trim()));
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showDropdown) {
        setSelectedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showDropdown) {
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  };

  const parseInputAsNew = (input: string): LocationPlaceOption => {
    // If we're in refinement mode (location already selected), create a place in that location
    if (currentSelection?.type === 'location') {
      return {
        type: 'place',
        locationId: currentSelection.locationId,
        locationName: currentSelection.locationName,
        placeName: input,
        displayText: `${currentSelection.locationName} :: ${input}`,
        isNew: true
      };
    }

    // Check if input contains → or -> (place format)
    if (input.includes('::') || input.includes('→') || input.includes('->')) {
      const parts = input.split(/::|\u2192|->/).map(p => p.trim());
      const locationName = parts[0];
      const placeName = parts[1];

      // Find if location exists
      const existingLocation = locations.find(l =>
        l.name.toLowerCase() === locationName.toLowerCase()
      );

      if (existingLocation) {
        return {
          type: 'place',
          locationId: existingLocation.id,
          locationName: existingLocation.name,
          placeName: placeName,
          displayText: `${existingLocation.name} :: ${placeName}`,
          isNew: true
        };
      } else {
        // Need to create location first, then place
        return {
          type: 'place',
          locationId: '', // Will be created
          locationName: locationName,
          placeName: placeName,
          displayText: `${locationName} :: ${placeName}`,
          isNew: true
        };
      }
    } else {
      // Location only
      return {
        type: 'location',
        locationId: '', // Will be created
        locationName: input,
        displayText: `${input}`,
        isNew: true
      };
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Show NET-NEW indicator for new options
  const shouldShowNetNew = (input: string): boolean => {
    if (!input.trim()) return false;

    const parsed = parseInputAsNew(input.trim());

    if (parsed.type === 'location') {
      return !locations.some(l => l.name.toLowerCase() === parsed.locationName.toLowerCase());
    } else {
      const location = locations.find(l => l.id === parsed.locationId ||
        l.name.toLowerCase() === parsed.locationName.toLowerCase());
      if (!location) return true;
      return !location.places?.some(p => p.name.toLowerCase() === parsed.placeName?.toLowerCase());
    }
  };

  return (
    <div className={className}>
      {!compact && <h3 style={{ marginBottom: '16px' }}>Location / Place</h3>}

      {/* Current Selection Pill - shown above input in full mode only */}
      {!compact && currentSelection ? (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '16px'
        }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              backgroundColor: 'var(--bg-accent)',
              color: 'var(--text-accent)',
              fontSize: '14px',
              borderRadius: '16px',
              border: '1px solid var(--border-light)'
            }}
          >
            <span>{currentSelection.displayText}</span>
            <button
              type="button"
              onClick={handleClearSelection}
              style={{
                marginLeft: '8px',
                background: 'none',
                border: 'none',
                color: 'var(--text-accent)',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1
              }}
              aria-label="Clear location/place selection"
            >
              ×
            </button>
          </div>
        </div>
      ) : !compact ? (
        <div style={{
          color: 'var(--text-secondary)',
          fontSize: '14px',
          fontStyle: 'italic',
          marginBottom: '16px'
        }}>
          No location or place assigned
        </div>
      ) : null}

      {/* Add New Location/Place */}
      <div style={{ position: 'relative', width: '100%', maxWidth: '100%' }} ref={dropdownRef}>
        <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={
              currentSelection?.type === 'location'
                ? `Search places within ${currentSelection.locationName}...`
                : allowCreate
                  ? "Search or create: 'Location' or 'Location :: Place'"
                  : "Filter by location or place..."
            }
            disabled={loading || currentSelection?.type === 'place'}
            style={{
              flex: 1,
              minWidth: 0,
              padding: '8px 12px',
              border: '1px solid var(--border-light)',
              borderRadius: '4px',
              fontSize: '16px',
              boxSizing: 'border-box',
              opacity: currentSelection?.type === 'place' ? 0.6 : 1
            }}
          />
        </div>

        {/* Autocomplete Dropdown */}
        {showDropdown && currentSelection?.type !== 'place' && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '4px',
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-light)',
              borderRadius: '4px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1000
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={`${option.type}-${option.locationId}-${option.placeId || 'none'}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: selectedIndex === index ? 'var(--bg-accent)' : 'var(--bg-card)',
                    color: selectedIndex === index ? 'var(--text-accent)' : 'var(--text-primary)',
                    fontSize: '14px',
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid var(--border-light)' : 'none'
                  }}
                >
                  {option.displayText}
                </div>
              ))
            ) : inputValue.trim() && allowCreate && shouldShowNetNew(inputValue.trim()) ? (
              <div
                onClick={() => handleCreateNew(parseInputAsNew(inputValue.trim()))}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: 'var(--warning-bg)',
                  color: 'var(--warning-text)',
                  fontSize: '14px',
                  fontStyle: 'italic'
                }}
              >
                {(() => {
                  const parsed = parseInputAsNew(inputValue.trim());
                  const actionText = parsed.type === 'location' ? 'Create new Location' : 'Create new Place';
                  return <><strong>{actionText}:</strong> {parsed.displayText}</>;
                })()}
              </div>
            ) : null}
          </div>
        )}

        {/* Help Text */}
        {!compact && currentSelection?.type !== 'place' && (
          <div style={{
            color: 'var(--text-primary)',
            fontSize: '12px',
            marginTop: '8px',
            fontStyle: 'italic'
          }}>
            {currentSelection?.type === 'location'
              ? `Refine to a specific place within ${currentSelection.locationName}, or click × to keep location-only.`
              : allowCreate
                ? 'Type location name or "Location :: Place" format. Select from dropdown or press Enter to create new.'
                : 'Type location name or "Location :: Place" format. Select from dropdown.'}
          </div>
        )}
      </div>

      {/* Compact mode: selection pill below input */}
      {compact && currentSelection && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginTop: '8px'
        }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              backgroundColor: 'var(--bg-accent)',
              color: 'var(--text-accent)',
              fontSize: '14px',
              borderRadius: '16px',
              border: '1px solid var(--border-light)'
            }}
          >
            <span>{currentSelection.displayText}</span>
            <button
              type="button"
              onClick={handleClearSelection}
              style={{
                marginLeft: '8px',
                background: 'none',
                border: 'none',
                color: 'var(--text-accent)',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1
              }}
              aria-label="Clear location/place selection"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPlaceEditor;
